import { approach, clamp, lerp, saturate, wrap01 } from "../physics/numbers";
import type { InputFrame, VehicleDynamics, VehicleState } from "./types";

const DRIFT_ENTRY_DURATION = 0.14;
const MINIMUM_DRIFT_SPEED = 16;
const SAFE_TRACK_RATIO = 0.62;

export const createVehicleState = (
  id: string,
  trackProgress = 0,
  lateralOffset = 0
): VehicleState => ({
  id,
  trackProgress: wrap01(trackProgress),
  safeTrackProgress: wrap01(trackProgress),
  lateralOffset,
  lateralVelocity: 0,
  heading: 0,
  slipAngle: 0,
  speed: 0,
  acceleration: 0,
  steering: 0,
  driftState: "grip",
  driftTime: 0,
  driftCharge: 0,
  boostHeat: 0,
  boostLocked: false,
  impactCooldown: 0,
  feedback: {
    boost: 0,
    impact: 0,
    impactSide: 0,
    recovery: 0
  }
});

export const cloneVehicleState = (vehicle: VehicleState): VehicleState => ({
  ...vehicle,
  feedback: { ...vehicle.feedback }
});

const updateDriftState = (vehicle: VehicleState, input: InputFrame, deltaSeconds: number): void => {
  const wantsDrift =
    input.drift &&
    vehicle.speed >= MINIMUM_DRIFT_SPEED &&
    Math.abs(input.steer) >= 0.18;

  if (wantsDrift) {
    vehicle.driftTime += deltaSeconds;
    vehicle.driftState = vehicle.driftTime < DRIFT_ENTRY_DURATION ? "entering" : "drifting";
    return;
  }

  vehicle.driftTime = Math.max(0, vehicle.driftTime - deltaSeconds * 2.5);

  if (vehicle.driftState !== "grip" && Math.abs(vehicle.slipAngle) > 0.025) {
    vehicle.driftState = "recovering";
    return;
  }

  vehicle.driftState = "grip";
};

const updateBoost = (
  vehicle: VehicleState,
  input: InputFrame,
  dynamics: VehicleDynamics,
  deltaSeconds: number
): boolean => {
  if (vehicle.boostLocked && vehicle.boostHeat <= 0.35) {
    vehicle.boostLocked = false;
  }

  const boostActive =
    input.boost &&
    vehicle.driftCharge > 0 &&
    !vehicle.boostLocked &&
    vehicle.speed > 4;

  if (boostActive) {
    vehicle.driftCharge = Math.max(0, vehicle.driftCharge - dynamics.boostConsumption * deltaSeconds);
    vehicle.boostHeat = Math.min(1, vehicle.boostHeat + dynamics.boostHeatRate * deltaSeconds);
    vehicle.feedback.boost = approach(vehicle.feedback.boost, 1, deltaSeconds * 7.5);

    if (vehicle.boostHeat >= 1) {
      vehicle.boostLocked = true;
    }
  } else {
    vehicle.boostHeat = Math.max(0, vehicle.boostHeat - dynamics.boostCoolingRate * deltaSeconds);
    vehicle.feedback.boost = approach(vehicle.feedback.boost, 0, deltaSeconds * 4.5);
  }

  return boostActive;
};

const updateDriftCharge = (
  vehicle: VehicleState,
  boostActive: boolean,
  dynamics: VehicleDynamics,
  deltaSeconds: number
): void => {
  if (vehicle.driftState === "drifting" && !boostActive) {
    const slipQuality = saturate((Math.abs(vehicle.slipAngle) - 0.04) / 0.38);
    const speedQuality = saturate((vehicle.speed - MINIMUM_DRIFT_SPEED) / 42);
    const earnedCharge = dynamics.driftChargeRate * slipQuality * (0.35 + speedQuality * 0.65);
    vehicle.driftCharge = Math.min(1, vehicle.driftCharge + earnedCharge * deltaSeconds);
    return;
  }

  if (!boostActive) {
    vehicle.driftCharge = Math.max(0, vehicle.driftCharge - dynamics.driftChargeDecay * deltaSeconds);
  }
};

const updateRoadBoundary = (
  vehicle: VehicleState,
  dynamics: VehicleDynamics,
  deltaSeconds: number
): void => {
  vehicle.impactCooldown = Math.max(0, vehicle.impactCooldown - deltaSeconds);
  vehicle.feedback.impact = approach(vehicle.feedback.impact, 0, deltaSeconds * 2.8);
  vehicle.feedback.recovery = approach(vehicle.feedback.recovery, 0, deltaSeconds * 2.2);

  if (vehicle.feedback.impact < 0.02) {
    vehicle.feedback.impactSide = 0;
  }

  if (Math.abs(vehicle.lateralOffset) <= dynamics.roadHalfWidth) {
    return;
  }

  const impactSide = vehicle.lateralOffset > 0 ? 1 : -1;
  const movingOutward = vehicle.lateralVelocity * impactSide > 0;
  vehicle.lateralOffset = impactSide * dynamics.roadHalfWidth;

  if (!movingOutward) {
    return;
  }

  vehicle.lateralVelocity *= -0.24;
  vehicle.heading = -impactSide * Math.min(0.16, Math.abs(vehicle.heading) + 0.045);
  vehicle.slipAngle *= -0.22;

  if (vehicle.impactCooldown === 0) {
    vehicle.speed *= 0.72;
    vehicle.impactCooldown = 0.45;
    vehicle.feedback.impact = 1;
    vehicle.feedback.impactSide = impactSide;
  }
};

export const stepVehicle = (
  vehicle: VehicleState,
  input: InputFrame,
  dynamics: VehicleDynamics,
  deltaSeconds: number,
  trackCurvature = 0
): void => {
  if (!Number.isFinite(deltaSeconds) || deltaSeconds <= 0) {
    return;
  }

  updateDriftState(vehicle, input, deltaSeconds);

  const drifting = vehicle.driftState === "entering" || vehicle.driftState === "drifting";
  const speedRatio = saturate(vehicle.speed / dynamics.maximumSpeed);
  const steeringAuthority = lerp(1, 0.52, speedRatio);
  const steeringTarget = input.steer * steeringAuthority;
  const steeringRate =
    Math.abs(steeringTarget) > Math.abs(vehicle.steering)
      ? dynamics.steeringResponse
      : dynamics.steeringReturn;
  vehicle.steering = approach(vehicle.steering, steeringTarget, steeringRate * deltaSeconds);
  vehicle.steering = clamp(vehicle.steering, -1, 1);

  const handlingSpeed = lerp(0.32, 1, saturate(vehicle.speed / 48));
  const headingTarget = vehicle.steering * (drifting ? 0.31 : 0.13) * handlingSpeed;
  const slipTarget = vehicle.steering * (drifting ? 0.5 : 0.075) * handlingSpeed;
  vehicle.heading = approach(vehicle.heading, headingTarget, deltaSeconds * (drifting ? 2.2 : 3.8));
  vehicle.slipAngle = approach(
    vehicle.slipAngle,
    slipTarget,
    deltaSeconds * (drifting ? 2.8 : vehicle.driftState === "recovering" ? 2.4 : 4.8)
  );
  vehicle.heading = clamp(vehicle.heading, -0.42, 0.42);
  vehicle.slipAngle = clamp(vehicle.slipAngle, -0.62, 0.62);

  const boostActive = updateBoost(vehicle, input, dynamics, deltaSeconds);
  updateDriftCharge(vehicle, boostActive, dynamics, deltaSeconds);

  const boundedCurvature = clamp(trackCurvature, -1, 1);

  const aerodynamicLoss = dynamics.aerodynamicDrag * vehicle.speed * vehicle.speed;
  const rollingLoss = vehicle.speed > 0 ? dynamics.rollingResistance : 0;
  const corneringLoss =
    Math.abs(boundedCurvature) * speedRatio * speedRatio * (drifting ? 1.1 : 3.8);
  const traction = drifting ? 0.84 : 1;
  const throttleForce = input.throttle * dynamics.engineAcceleration * traction;
  const brakeForce = input.brake * dynamics.brakeDeceleration;
  const boostForce = boostActive ? dynamics.boostAcceleration : 0;
  const acceleration =
    throttleForce + boostForce - brakeForce - aerodynamicLoss - rollingLoss - corneringLoss;
  const speedLimit = boostActive ? dynamics.boostedMaximumSpeed : dynamics.maximumSpeed;
  const previousSpeed = vehicle.speed;
  vehicle.speed = clamp(vehicle.speed + acceleration * deltaSeconds, 0, speedLimit);
  vehicle.acceleration = (vehicle.speed - previousSpeed) / deltaSeconds;

  const combinedAngle = vehicle.heading + vehicle.slipAngle;
  const lateralGrip = drifting ? 0.54 : 0.34;
  const curveLoad = boundedCurvature * speedRatio * speedRatio * 3.2;
  const lateralTarget = vehicle.speed * Math.sin(combinedAngle) * lateralGrip - curveLoad;
  const lateralResponse = drifting ? 12 : 18;
  vehicle.lateralVelocity = approach(
    vehicle.lateralVelocity,
    lateralTarget,
    lateralResponse * deltaSeconds
  );
  vehicle.lateralOffset += vehicle.lateralVelocity * deltaSeconds;

  const forwardEfficiency = Math.max(0.36, Math.cos(combinedAngle));
  vehicle.trackProgress = wrap01(
    vehicle.trackProgress + (vehicle.speed * forwardEfficiency * deltaSeconds) / dynamics.trackLength
  );

  updateRoadBoundary(vehicle, dynamics, deltaSeconds);

  if (
    Math.abs(vehicle.lateralOffset) <= dynamics.roadHalfWidth * SAFE_TRACK_RATIO &&
    Math.abs(vehicle.heading) <= 0.2
  ) {
    vehicle.safeTrackProgress = vehicle.trackProgress;
  }
};

export const recoverVehicle = (vehicle: VehicleState): void => {
  vehicle.trackProgress = vehicle.safeTrackProgress;
  vehicle.lateralOffset = 0;
  vehicle.lateralVelocity = 0;
  vehicle.heading = 0;
  vehicle.slipAngle = 0;
  vehicle.steering = 0;
  vehicle.speed = clamp(vehicle.speed * 0.42, 8, 20);
  vehicle.acceleration = 0;
  vehicle.driftState = "grip";
  vehicle.driftTime = 0;
  vehicle.driftCharge *= 0.85;
  vehicle.impactCooldown = 0.5;
  vehicle.feedback.boost = 0;
  vehicle.feedback.impact = 0;
  vehicle.feedback.impactSide = 0;
  vehicle.feedback.recovery = 1;
};
